<template>
  <div class="sch-page">
    <!-- ═══ APPBAR ═══ -->
    <header class="appbar">
      <router-link to="/" class="vm-mark">
        <img
          class="vm-logo"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </router-link>
      <div
        id="appbar-menu"
        class="appbar-menu"
        :class="{ 'is-open': menuOpen }"
      >
        <nav class="appnav">
          <router-link to="/" exact active-class="is-active">
            Início
          </router-link>
          <router-link
            :to="{ path: '/', query: { buscar: '1' } }"
            active-class="is-active"
          >
            Buscar profissionais de saúde
          </router-link>
          <router-link v-if="loggedIn" to="/consultas" active-class="is-active">
            Minhas consultas
          </router-link>
          <router-link
            v-if="loggedIn"
            to="/documentos"
            active-class="is-active"
          >
            Documentos
          </router-link>
          <router-link
            v-if="loggedIn"
            to="/meus-pagamentos"
            active-class="is-active"
          >
            Pagamentos
          </router-link>
        </nav>
        <span class="appbar-spacer" />
        <div v-if="!loggedIn" class="appbar-auth">
          <router-link to="/login" class="btn btn--outline btn--sm">
            Entrar
          </router-link>
          <router-link to="/cadastrar" class="btn btn--primary btn--sm">
            Criar conta
          </router-link>
        </div>
      </div>
      <AppBarUserMenu v-if="loggedIn" />
      <button
        type="button"
        class="navbar-toggler appbar-toggler"
        :class="menuOpen ? 'not-collapsed' : 'collapsed'"
        aria-controls="appbar-menu"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        :aria-label="menuOpen ? 'Fechar menu' : 'Abrir menu'"
        @click="menuOpen = !menuOpen"
      >
        <div class="animated-icon">
          <span />
          <span />
          <span />
          <span />
        </div>
      </button>
    </header>

    <!-- ═══ WIZARD ═══ -->
    <div class="wizard-row">
      <div class="steps">
        <div class="step step--done">
          <span class="step-dot">✓</span>
          <span class="step-label">Profissional</span>
        </div>
        <span class="step-bar step-bar--done" />
        <div class="step step--active">
          <span class="step-dot">2</span>
          <span class="step-label">Horário</span>
        </div>
        <span class="step-bar" />
        <div class="step">
          <span class="step-dot">3</span>
          <span class="step-label">Pagamento</span>
        </div>
        <span class="step-bar" />
        <div class="step">
          <span class="step-dot">4</span>
          <span class="step-label">Confirmação</span>
        </div>
      </div>
    </div>

    <!-- ═══ CORPO: CALENDÁRIO + RESUMO ═══ -->
    <div class="sched-body">
      <!-- COLUNA PRINCIPAL -->
      <div class="sched-main">
        <div>
          <h1>Escolha a data e o horário</h1>
          <p class="subtitle">
            Consulta por vídeo com {{ doctor.name || "a médica" }} · 30 min
          </p>
        </div>

        <!-- STRIP DE DIAS -->
        <div class="card">
          <div class="dstrip-head">
            <h3>{{ stripMonthLabel }}</h3>
            <div class="dstrip-nav">
              <button @click="shiftWeek(-1)" aria-label="Semana anterior">
                ‹
              </button>
              <button @click="shiftWeek(1)" aria-label="Próxima semana">
                ›
              </button>
            </div>
          </div>
          <div class="dstrip">
            <div
              v-for="day in visibleDays"
              :key="day.dateStr"
              class="day"
              :class="{ on: day.dateStr === selectedDay, off: day.count === 0 }"
              @click="day.count > 0 && selectDay(day.dateStr)"
            >
              <span class="dn">{{ day.dn }}</span>
              <span class="dd">{{ day.dd }}</span>
              <span class="av">
                {{ day.count > 0 ? day.count + " horários" : "—" }}
              </span>
            </div>
          </div>
        </div>

        <!-- SLOTS DO DIA -->
        <div class="card slot-section">
          <div class="slot-block">
            <span class="lbl">
              Horários disponíveis
              <span class="ct">
                ·
                <span>{{ selectedDayLabel }}</span>
              </span>
            </span>
            <div class="slot-wide">
              <template v-if="slotsForSelectedDay.length">
                <span
                  v-for="slot in slotsForSelectedDay"
                  :key="slot.id"
                  class="slot"
                  :class="{
                    'slot--active': selectedSlot && selectedSlot.id === slot.id,
                    'slot--off': !isAvailable(slot)
                  }"
                  @click="isAvailable(slot) && selectSlot(slot)"
                >
                  {{ formatTime(slot.start_time) }}
                </span>
              </template>
              <template v-else-if="allSlots.length">
                <span class="slot-empty">
                  Nenhum horário disponível neste dia
                </span>
              </template>
              <template v-else>
                <div class="slot-request">
                  <p class="slot-request-msg">
                    Nenhum horário disponível no momento.
                  </p>
                  <button
                    class="btn btn--outline btn--sm"
                    :disabled="slotRequested || slotRequestLoading"
                    @click="requestNewSlots"
                  >
                    {{
                      slotRequested
                        ? "Solicitação enviada ✓"
                        : slotRequestLoading
                        ? "Enviando..."
                        : "Solicitar novos horários"
                    }}
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
      <!-- /sched-main -->

      <!-- ═══ RESUMO (sidebar) ═══ -->
      <aside class="card summary-card">
        <div class="summary-doc">
          <div class="avatar avatar--md avatar--ring">
            <img
              v-if="doctor.photo"
              :src="doctor.photo"
              :alt="doctor.name"
              loading="lazy"
              decoding="async"
            />
            <template v-else>{{ doctorInitials }}</template>
          </div>
          <div class="stack" style="gap:1px">
            <span class="nm">{{ doctor.name || "—" }}</span>
            <span class="sub">{{ doctorSpecialty }} · {{ doctorCRM }}</span>
          </div>
        </div>

        <div class="stack" style="gap:var(--space-3)">
          <div class="summary-row">
            <span class="k">Data</span>
            <span class="v">{{ selectedDayLabelFull }}</span>
          </div>
          <div class="summary-row">
            <span class="k">Horário</span>
            <span class="v hl">{{ selectedTimeLabel }}</span>
          </div>
          <div class="summary-row">
            <span class="k">Modalidade</span>
            <span class="v">Vídeo · 30 min</span>
          </div>
        </div>

        <div class="summary-total">
          <span class="tl">Total</span>
          <span class="tv">{{ doctorPrice }}</span>
        </div>

        <button
          class="btn btn--accent btn--lg btn--block"
          :disabled="!canProceed"
          @click="proceed"
        >
          Continuar
        </button>

        <div class="book-trust">
          <span class="t">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M20 6L9 17l-5-5"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Cancelamento grátis até 24h antes
          </span>
          <span class="t">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linejoin="round"
              />
            </svg>
            Você só paga após confirmar
          </span>
        </div>
      </aside>
    </div>
    <!-- /sched-body -->

    <!-- ═══ RODAPÉ ═══ -->
    <footer class="foot-mini">
      <span class="vm-mark">
        <img
          class="vm-logo vm-logo--sm"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </span>
      <span class="cpy">© 2026 Videomedi · CNPJ 00.000.000/0001-00</span>
      <span class="links">
        <a href="#">Privacidade</a>
        <a href="#">Termos de Uso</a>
        <a href="#">LGPD</a>
        <a href="#">Suporte</a>
      </span>
    </footer>
  </div>
</template>

<script>
import { doctorMethods } from "./../state/helpers";
import { intToBRL } from "./../utils/currency/convert";
import { mapGetters, mapActions } from "vuex";
import AppBarUserMenu from "./../components/AppBarUserMenu.vue";

// slot is available when status is absent (API omits it) or explicitly 0
const isAvailable = s => s.status == null || Number(s.status) === 0;

const pad = n => String(n).padStart(2, "0");

const localDateStr = d =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const resolveSlotDate = slot => {
  if (slot.slot_date) return slot.slot_date;
  if (slot.start_time) return localDateStr(new Date(slot.start_time));
  return null;
};

const groupSlotsByDate = slots => {
  const groups = {};
  slots.forEach(slot => {
    const date = resolveSlotDate(slot);
    if (!date) return;
    if (!groups[date]) groups[date] = [];
    groups[date].push(slot);
  });
  return groups;
};

const sortGroupsByStartTime = groups => {
  Object.values(groups).forEach(daySlots =>
    daySlots.sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
  );
  return groups;
};

export default {
  name: "ScheduleTimeView",
  components: { AppBarUserMenu },

  data() {
    return {
      doctor: { doctor_property: {} },
      allSlots: [],
      selectedDay: null,
      selectedSlot: null,
      selectedFallbackTime: "08:00",
      weekOffset: 0,
      slotRequested: false,
      slotRequestLoading: false,
      menuOpen: false
    };
  },

  async mounted() {
    const doctorId = this.$route.params.doctor;
    if (doctorId) {
      const res = await this.getDoctorDetails(doctorId);
      this.doctor = res.data;
      const dp = this.doctor.doctor_property;
      dp.council_description = `${dp.council_name} ${dp.council_number} ${dp.council_uf}`;
      try {
        const slotsRes = await this.fetchDoctorsAvailables({ id: doctorId });
        if (slotsRes?.data?.length) {
          const now = new Date();
          const expanded = [];
          slotsRes.data.forEach(daySlot => {
            if (!daySlot.slot_date || !Array.isArray(daySlot.slots)) return;
            daySlot.slots.forEach(timeStr => {
              const startTime = `${daySlot.slot_date}T${timeStr}:00`;
              if (new Date(startTime) > now) {
                expanded.push({
                  id: `${daySlot.id}_${timeStr}`,
                  slot_id: daySlot.id,
                  slot_date: daySlot.slot_date,
                  start_time: startTime
                });
              }
            });
          });
          this.allSlots = expanded;
          const firstDay = this.availableDays[0];
          if (firstDay) {
            this.selectedDay = firstDay;
            const firstSlot = (this.groupedSlots[firstDay] || []).find(s =>
              isAvailable(s)
            );
            if (firstSlot) this.selectedSlot = firstSlot;
          }
        }
      } catch {
        /* slots vêm do fallback visual */
      }
    }

    if (!this.selectedDay) {
      this.selectedDay = this.visibleDays[0]?.dateStr;
    }
  },

  methods: {
    ...doctorMethods,
    ...mapActions("appointments", {
      holdScheduleAppointment: "holdScheduleAppointment"
    }),
    ...mapActions("doctor", {
      fetchDoctorsAvailables: "fetchDoctorsAvailables",
      sendSMStoDoctor: "sendSMStoDoctor"
    }),

    isAvailable,

    selectDay(dateStr) {
      this.selectedDay = dateStr;
      this.selectedSlot = null;
      const first = (this.groupedSlots[dateStr] || []).find(s =>
        isAvailable(s)
      );
      if (first) this.selectedSlot = first;
    },

    selectSlot(slot) {
      this.selectedSlot = slot;
    },

    selectFallbackSlot(time) {
      this.selectedSlot = null;
      this.selectedFallbackTime = time;
    },

    shiftWeek(dir) {
      this.weekOffset += dir;
    },

    formatTime(datetime) {
      if (!datetime) return "";
      return new Date(datetime).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    async requestNewSlots() {
      if (!this.loggedIn) {
        this.$router.push({
          name: "ScheduleLogin",
          query: { redirectFrom: this.$route.fullPath }
        });
        return;
      }
      this.slotRequestLoading = true;
      try {
        await this.sendSMStoDoctor(this.doctor.id);
        this.slotRequested = true;
      } finally {
        this.slotRequestLoading = false;
      }
    },

    proceed() {
      const slotId =
        this.selectedSlot?.slot_id || this.selectedSlot?.id || null;
      const scheduledAt =
        this.selectedSlot?.start_time ||
        `${this.selectedDay}T${this.selectedFallbackTime}:00`;
      this.holdScheduleAppointment({
        slot_id: slotId,
        doctor_id: this.doctor.id,
        doctor: this.doctor,
        scheduled_at: scheduledAt
      });
      if (!this.loggedIn) {
        this.$router.push({ name: "ScheduleLogin" });
      } else {
        this.$router.push({ name: "Schedule" });
      }
    }
  },

  computed: {
    ...mapGetters("auth", ["loggedIn"]),
    ...mapGetters("user", ["getCurrentUser"]),

    doctorInitials() {
      return (
        (this.doctor.name || "")
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || ""
      );
    },
    doctorSpecialty() {
      return this.doctor.doctor_property?.medical_specialty_name || "—";
    },
    doctorCRM() {
      return this.doctor.doctor_property?.council_description || "—";
    },
    doctorPrice() {
      const price = this.doctor.doctor_property
        ?.price_cents_and_earning_margin_sum;
      return price ? intToBRL(price) : "—";
    },

    groupedSlots() {
      return sortGroupsByStartTime(groupSlotsByDate(this.allSlots));
    },
    availableDays() {
      return Object.keys(this.groupedSlots).sort();
    },

    visibleDays() {
      const base = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const offset = this.weekOffset * 7;
      for (let i = 0; i < 7; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + offset + i);
        const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
          d.getDate()
        )}`;
        const dn = d
          .toLocaleDateString("pt-BR", { weekday: "short" })
          .replace(".", "")
          .replace(/^\w/, c => c.toUpperCase());
        const slots = this.groupedSlots[dateStr] || [];
        const count = slots.filter(s => isAvailable(s)).length;
        base.push({ dateStr, dn, dd: d.getDate(), count });
      }
      return base;
    },

    stripMonthLabel() {
      if (!this.selectedDay) {
        const today = new Date();
        return today
          .toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
          .replace(/^\w/, c => c.toUpperCase());
      }
      const d = new Date(this.selectedDay + "T00:00:00");
      return d
        .toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
        .replace(/^\w/, c => c.toUpperCase());
    },

    slotsForSelectedDay() {
      return this.selectedDay ? this.groupedSlots[this.selectedDay] || [] : [];
    },

    selectedDayLabel() {
      if (!this.selectedDay)
        return this.visibleDays[0]?.dn + ", " + this.visibleDays[0]?.dd || "—";
      const d = new Date(this.selectedDay + "T00:00:00");
      return d
        .toLocaleDateString("pt-BR", {
          weekday: "short",
          day: "numeric",
          month: "short"
        })
        .replace(/\.\s*/g, " ")
        .trim()
        .replace(/^\w/, c => c.toUpperCase());
    },
    selectedDayLabelFull() {
      if (!this.selectedDay) return "—";
      return this.selectedDayLabel;
    },
    selectedTimeLabel() {
      if (this.selectedSlot)
        return this.formatTime(this.selectedSlot.start_time);
      if (this.slotsForSelectedDay.length === 0)
        return this.selectedFallbackTime;
      return "—";
    },
    canProceed() {
      return !!this.selectedSlot || this.slotsForSelectedDay.length === 0;
    }
  },

  watch: {
    $route() {
      this.menuOpen = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.vm-logo {
  display: block;
  height: 28px;
  width: auto;
}

.vm-logo--sm {
  height: 22px;
}

/* ─── PAGE SHELL ─── */
.sch-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--color-bg-page, #f4f6fb);
}

/* ─── APPBAR ─── */
.appbar {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  background: #fff;
  border-bottom: 1px solid var(--color-gray-200);
  padding: 0 var(--space-8);
  height: 68px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.vm-mark {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  text-decoration: none;
}

.vm-wordmark {
  font-size: var(--font-size-md);
  font-weight: var(--fw-extra);
  color: var(--color-accent);
  letter-spacing: -0.01em;
}

.appnav {
  display: flex;
  align-items: center;
  gap: var(--space-6);

  a {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-600);
    text-decoration: none;
    cursor: pointer;

    &:hover {
      color: var(--color-primary);
    }
    &.is-active {
      color: var(--color-accent);
    }
    &.router-link-exact-active {
      color: var(--color-primary);
    }
  }
}

.appbar-spacer {
  flex: 1;
}

.appbar-auth {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.appbar-menu {
  display: contents;
}

.appbar-toggler {
  display: none;

  .animated-icon span {
    background: var(--color-gray-600) !important;
  }
}

@media (max-width: 991px) {
  .appbar {
    gap: var(--space-2);
    padding: 0 var(--space-4);
  }

  .appbar .vm-mark {
    margin-right: auto;
  }

  .appbar-toggler {
    display: block;
  }

  .appbar-menu {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-4);
    background: #fff;
    border-bottom: 1px solid var(--color-gray-200);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.06);

    &.is-open {
      display: flex;
    }
  }

  .appnav {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .appbar-spacer {
    display: none;
  }

  .appbar-auth {
    flex-direction: column;
    align-items: stretch;

    .btn {
      justify-content: center;
    }
  }
}

.appbar-user {
  display: flex;
  align-items: center;
  gap: var(--space-2);

  .nm {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-bold);
    color: var(--color-gray-800);
  }
}

/* ─── WIZARD ─── */
.wizard-row {
  display: flex;
  justify-content: center;
  padding: var(--space-6) var(--space-10) 0;
}

.steps {
  display: flex;
  align-items: center;
  max-width: 620px;
  width: 100%;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);

  .step-dot {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid var(--color-gray-300);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--font-size-xs);
    font-weight: var(--fw-bold);
    color: var(--color-gray-400);
    background: #fff;
  }

  .step-label {
    font-size: var(--font-size-xs);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-400);
    white-space: nowrap;
  }

  &.step--done .step-dot {
    background: var(--color-success);
    border-color: var(--color-success);
    color: #fff;
  }

  &.step--done .step-label {
    color: var(--color-gray-700);
  }

  &.step--active .step-dot {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: #fff;
  }

  &.step--active .step-label {
    color: var(--color-primary);
    font-weight: var(--fw-bold);
  }
}

.step-bar {
  flex: 1;
  height: 2px;
  background: var(--color-gray-200);
  margin: 0 var(--space-1);
  margin-top: -16px;

  &.step-bar--done {
    background: var(--color-success);
  }
}

/* ─── SCHED BODY ─── */
.sched-body {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: var(--space-8);
  padding: var(--space-6) var(--space-10) var(--space-10);
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    padding: var(--space-5);
  }
}

.sched-main {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);

  h1 {
    font-size: var(--font-size-2xl);
    font-weight: var(--fw-extra);
    color: var(--color-gray-900);
    letter-spacing: -0.01em;
    margin: 0 0 var(--space-1);
    line-height: 1.2;
  }

  .subtitle {
    font-size: var(--font-size-sm);
    color: var(--color-gray-500);
    margin: 0;
  }
}

/* ─── CARD: sem padding global no app ─── */
.card {
  padding: var(--space-6);
}

/* ─── DSTRIP ─── */
.dstrip-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-4);

  h3 {
    font-size: var(--font-size-md);
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
  }
}

.dstrip-nav {
  display: flex;
  gap: var(--space-2);

  button {
    width: 34px;
    height: 34px;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-gray-200);
    background: #fff;
    color: var(--color-gray-600);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    cursor: pointer;

    &:hover {
      border-color: var(--color-primary);
      color: var(--color-primary);
    }
  }
}

.dstrip {
  display: flex;
  gap: var(--space-2);

  .day {
    flex: 1;
    border: 1.5px solid var(--color-gray-200);
    border-radius: var(--radius-md);
    padding: var(--space-3) var(--space-2);
    text-align: center;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 2px;

    &:hover:not(.off) {
      border-color: var(--color-primary);
    }

    .dn {
      font-size: var(--font-size-xs);
      font-weight: var(--fw-bold);
      color: var(--color-gray-400);
      text-transform: uppercase;
    }

    .dd {
      font-family: var(--font-num, "Inter", sans-serif);
      font-size: var(--font-size-xl);
      font-weight: var(--fw-bold);
      color: var(--color-gray-800);
    }

    .av {
      font-size: 10px;
      font-weight: var(--fw-semibold);
      color: var(--color-success);
    }

    &.on {
      background: var(--color-primary);
      border-color: var(--color-primary);

      .dn,
      .dd {
        color: #fff;
      }
      .av {
        color: rgba(255, 255, 255, 0.85);
      }
    }

    &.off {
      background: var(--color-gray-50, #f8fafc);
      pointer-events: none;
      cursor: default;

      .dd {
        color: var(--color-gray-300);
      }
      .av {
        color: var(--color-gray-300);
      }
    }
  }
}

/* ─── SLOT SECTION ─── */
.slot-section {
  padding: var(--space-6);
}

.slot-block {
  .lbl {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-sm);
    font-weight: var(--fw-bold);
    color: var(--color-gray-700);
    margin-bottom: var(--space-3);

    .ct {
      font-weight: var(--fw-semibold);
      color: var(--color-gray-400);
      font-size: var(--font-size-xs);
    }
  }
}

.slot-wide {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--space-2);

  @media (max-width: 700px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* slot - definido localmente */
.slot {
  border: 1.5px solid var(--color-gray-200);
  background: #fff;
  color: var(--color-gray-700);
  border-radius: var(--radius-sm);
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-num, "Inter", sans-serif);
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s;

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
}

.slot--active {
  background: var(--color-primary) !important;
  border-color: var(--color-primary) !important;
  color: #fff !important;
}
.slot--off {
  background: var(--color-gray-100) !important;
  color: var(--color-gray-300) !important;
  pointer-events: none;
}

.slot-empty {
  font-size: var(--font-size-sm);
  color: var(--color-gray-400);
  padding: var(--space-3) 0;
}

.slot-request {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4) 0;

  .slot-request-msg {
    font-size: var(--font-size-sm);
    color: var(--color-gray-500);
    margin: 0;
  }
}

/* ─── SUMMARY CARD ─── */
.summary-card {
  position: sticky;
  top: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-6);
  border-color: var(--color-primary) !important;

  @media (max-width: 860px) {
    position: static;
  }
}

.summary-doc {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid var(--color-gray-100);

  .nm {
    display: block;
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
    font-size: var(--font-size-sm);
  }

  .sub {
    display: block;
    font-size: var(--font-size-xs);
    color: var(--color-gray-500);
  }
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-sm);

  .k {
    color: var(--color-gray-500);
  }
  .v {
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
  }
  .v.hl {
    color: var(--color-success);
  }
}

.summary-total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);

  .tl {
    font-size: var(--font-size-sm);
    color: var(--color-gray-500);
    font-weight: var(--fw-semibold);
  }
  .tv {
    font-family: var(--font-num, "Inter", sans-serif);
    font-size: var(--font-size-2xl);
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
  }
}

/* ─── BOOK TRUST ─── */
.book-trust {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-gray-100);

  .t {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-xs);
    color: var(--color-gray-500);

    svg {
      color: var(--color-success);
      flex: none;
    }
  }
}

/* ─── FOOT MINI ─── */
.foot-mini {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-5) var(--space-8);
  border-top: 1px solid var(--color-gray-200);
  background: var(--color-bg-page, #f4f6fb);
  margin-top: auto;

  .vm-mark {
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }
  .vm-wordmark {
    color: var(--color-gray-500);
    font-size: var(--font-size-sm);
    font-weight: normal;
  }
  .cpy {
    font-size: var(--font-size-xs);
    color: var(--color-gray-400);
  }
  .links {
    margin-left: auto;
    display: flex;
    gap: var(--space-5);

    a {
      font-size: var(--font-size-xs);
      font-weight: var(--fw-semibold);
      color: var(--color-gray-500);
      text-decoration: none;
      &:hover {
        color: var(--color-primary);
      }
    }
  }
}
</style>
